import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, Play, Square, RotateCw, RefreshCw,
  ToggleRight, ToggleLeft, Activity, AlertTriangle, CheckCircle,
  Eye, EyeOff, Plus, Pencil, Trash2, PanelLeftClose, PanelLeft,
  FileCode2, ChevronDown, ChevronUp, Server as ServerIcon, XCircle,
} from 'lucide-react';
import { API, apiFetch } from '../apiClient';

type ServerRow = { id: string; label: string; host: string; port: number; username: string };
type ServerStatus = 'unknown' | 'online' | 'offline' | 'testing';

type SystemdAction = 'start' | 'stop' | 'restart' | 'reload' | 'enable' | 'disable' | 'status';

type SystemdServiceRow = {
  id: string;
  label: string;
  unit: string;
  description: string;
  managedUnit: boolean;
  serverIds: string[] | null;
};

interface SystemdPageProps {
  servers: ServerRow[];
  passwords: Record<string, string>;
  setPassword: (id: string, v: string) => void;
  showPw: Record<string, boolean>;
  setShowPw: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  serverStatus: Record<string, { status: ServerStatus; info: string }>;
  testConnection: (server: ServerRow) => void;
}

function serversForCatalog(svc: SystemdServiceRow | null, all: ServerRow[]): ServerRow[] {
  if (!svc || !svc.serverIds || svc.serverIds.length === 0) return all;
  return all.filter((s) => svc.serverIds!.includes(s.id));
}

type ModalMode = 'add' | 'edit' | null;

function computeUnitActionFlags(props: Record<string, string> | null) {
  const activeState = (props?.ActiveState ?? '').trim().toLowerCase();
  const unitFileState = (props?.UnitFileState ?? '').trim().toLowerCase();
  const loadState = (props?.LoadState ?? '').trim().toLowerCase();
  const has = !!(props && Object.keys(props).length > 0);

  const running = activeState === 'active' || activeState === 'reloading';
  const transitional = activeState === 'activating' || activeState === 'deactivating';
  const showStop = has && (running || transitional);
  const showStart = has && !showStop && (
    activeState === 'inactive'
    || activeState === 'dead'
    || activeState === 'failed'
    || loadState === 'not-found'
  );

  const masked = unitFileState === 'masked';
  const showEnable = has && !masked && (unitFileState === 'disabled' || unitFileState === 'bad');
  const showDisable = has && !masked && (unitFileState === 'enabled' || unitFileState === 'indirect');

  const showReload = has && (activeState === 'active' || activeState === 'reloading');

  return { has, showStart, showStop, showEnable, showDisable, showReload, activeState, unitFileState };
}

const emptyForm = () => ({
  label: '',
  unit: '',
  description: '',
  managedUnit: false,
  restrictServers: false,
  selectedServerIds: [] as string[],
});

export default function SystemdPage({
  servers,
  passwords,
  setPassword,
  showPw,
  setShowPw,
  serverStatus,
  testConnection,
}: SystemdPageProps) {
  const [catalog, setCatalog] = useState<SystemdServiceRow[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogErr, setCatalogErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [serverId, setServerId] = useState('');
  const [railOpen, setRailOpen] = useState(true);
  const [modal, setModal] = useState<ModalMode>(null);
  const [form, setForm] = useState(emptyForm());
  const [savingCatalog, setSavingCatalog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [busy, setBusy] = useState<SystemdAction | null>(null);
  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const [autoStatus, setAutoStatus] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [propsOut, setPropsOut] = useState<Record<string, string> | null>(null);
  const [textOut, setTextOut] = useState<string>('');
  const silentStatusSeq = useRef(0);

  const [unitOpen, setUnitOpen] = useState(true);
  const [unitContent, setUnitContent] = useState('');
  const [unitFragment, setUnitFragment] = useState<string | null>(null);
  const [unitLoaded, setUnitLoaded] = useState(false);
  const [unitLoading, setUnitLoading] = useState(false);
  const [unitSaving, setUnitSaving] = useState(false);
  const [unitMsg, setUnitMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveHint, setSaveHint] = useState(false);

  const selected = catalog.find((s) => s.id === selectedId) || null;
  const targets = serversForCatalog(selected, servers);

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    setCatalogErr(null);
    try {
      const r = await apiFetch(`${API}/api/systemd-services`).then((x) => x.json());
      if (Array.isArray(r)) setCatalog(r);
      else setCatalog([]);
    } catch (e: unknown) {
      setCatalogErr(e instanceof Error ? e.message : String(e));
      setCatalog([]);
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!selected) return;
    const list = serversForCatalog(selected, servers);
    if (!list.length) {
      setServerId('');
      return;
    }
    if (!list.some((s) => s.id === serverId)) setServerId(list[0].id);
  }, [selected, servers, serverId]);

  useEffect(() => {
    silentStatusSeq.current += 1;
    setUnitLoaded(false);
    setUnitContent('');
    setUnitFragment(null);
    setUnitMsg(null);
    setSaveHint(false);
    setPropsOut(null);
    setTextOut('');
    setErr(null);
  }, [selectedId, serverId]);

  const pwPayload = () => Object.fromEntries(Object.entries(passwords).filter(([, v]) => v.trim()));

  const postPw = useCallback(async (path: string, body: object) => {
    const r = await apiFetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passwords: pwPayload(), ...body }),
    });
    return r.json();
  }, [passwords]);

  const refreshStatus = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      const silent = !!opts.silent;
      if (!selected || !serverId || !passwords[serverId]?.trim()) return false;
      const u = selected.unit.trim();
      if (!u) return false;

      const seq = ++silentStatusSeq.current;
      if (silent) setStatusRefreshing(true);
      else setBusy('status');

      try {
        const r = await postPw(`/api/systemd/${serverId}`, { unit: u, action: 'status' });
        if (silentStatusSeq.current !== seq) return false;
        if (r.error) {
          if (!silent) {
            setErr(r.error);
            setTextOut('');
            setPropsOut(null);
          }
          return false;
        }
        setPropsOut(r.properties || {});
        setTextOut(r.statusText || '');
        setErr(null);
        return true;
      } catch (e: unknown) {
        if (!silent) {
          setErr(e instanceof Error ? e.message : String(e));
          setTextOut('');
          setPropsOut(null);
        }
        return false;
      } finally {
        if (silentStatusSeq.current === seq) {
          if (silent) setStatusRefreshing(false);
          else setBusy(null);
        }
      }
    },
    [postPw, selected, serverId, passwords],
  );

  useEffect(() => {
    if (!autoStatus || !selected || !serverId || !passwords[serverId]?.trim()) return undefined;
    const id = window.setInterval(() => {
      void refreshStatus({ silent: true });
    }, 8000);
    void refreshStatus({ silent: true });
    return () => window.clearInterval(id);
  }, [autoStatus, selectedId, serverId, selected?.unit, passwords, refreshStatus]);

  const filtered = catalog.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      c.label.toLowerCase().includes(q) ||
      c.unit.toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q)
    );
  });

  const openAdd = () => {
    setForm(emptyForm());
    setModal('add');
  };

  const openEdit = (svc: SystemdServiceRow) => {
    setForm({
      label: svc.label,
      unit: svc.unit,
      description: svc.description || '',
      managedUnit: svc.managedUnit,
      restrictServers: !!(svc.serverIds && svc.serverIds.length),
      selectedServerIds: svc.serverIds && svc.serverIds.length ? [...svc.serverIds] : [],
    });
    setModal('edit');
    setSelectedId(svc.id);
  };

  const saveModal = async () => {
    const lb = form.label.trim();
    const u = form.unit.trim();
    if (!lb) {
      setCatalogErr('Label is required');
      return;
    }
    if (!u) {
      setCatalogErr('Unit name is required');
      return;
    }
    if (form.restrictServers && form.selectedServerIds.length === 0) {
      setCatalogErr('Select at least one server, or turn off “Restrict to selected servers”.');
      return;
    }
    const serverIds = form.restrictServers && form.selectedServerIds.length > 0 ? form.selectedServerIds : null;
    setSavingCatalog(true);
    setCatalogErr(null);
    try {
      if (modal === 'add') {
        const r = await apiFetch(`${API}/api/systemd-services`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: lb,
            unit: u,
            description: form.description.trim(),
            managedUnit: form.managedUnit,
            serverIds,
          }),
        }).then((x) => x.json());
        if (r.error) {
          setCatalogErr(r.error);
          return;
        }
        if (r.id) setSelectedId(r.id);
      } else if (modal === 'edit' && selectedId) {
        const r = await apiFetch(`${API}/api/systemd-services/${selectedId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: lb,
            unit: u,
            description: form.description.trim(),
            managedUnit: form.managedUnit,
            serverIds,
          }),
        }).then((x) => x.json());
        if (r.error) {
          setCatalogErr(r.error);
          return;
        }
      }
      setModal(null);
      await loadCatalog();
    } catch (e: unknown) {
      setCatalogErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingCatalog(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    setCatalogErr(null);
    try {
      const r = await apiFetch(`${API}/api/systemd-services/${deleteId}`, { method: 'DELETE' }).then((x) => x.json());
      if (r.error) setCatalogErr(r.error);
      else {
        if (selectedId === deleteId) setSelectedId(null);
        setDeleteId(null);
        await loadCatalog();
      }
    } catch (e: unknown) {
      setCatalogErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  const run = async (action: SystemdAction) => {
    if (!selected) return;
    if (!serverId || !passwords[serverId]?.trim()) {
      setErr('Enter the SSH password for the selected server.');
      return;
    }
    const u = selected.unit.trim();
    if (!u) {
      setErr('Unit name is missing.');
      return;
    }
    if (action === 'status') {
      await refreshStatus({ silent: false });
      return;
    }
    setBusy(action);
    setErr(null);
    setSaveHint(false);
    try {
      const r = await postPw(`/api/systemd/${serverId}`, { unit: u, action });
      if (r.error) {
        setErr(r.error);
        setTextOut('');
        return;
      }
      setTextOut(r.output || (r.success ? 'OK.' : 'Command failed.'));
      if (!r.success) setErr(`systemctl exited with code ${r.code ?? 'unknown'}`);
      if (r.success) await refreshStatus({ silent: true });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
      setTextOut('');
    } finally {
      setBusy(null);
    }
  };

  const loadUnitFile = async () => {
    if (!selected?.managedUnit || !serverId || !passwords[serverId]?.trim()) return;
    setUnitLoading(true);
    setUnitMsg(null);
    setSaveHint(false);
    try {
      const r = await postPw(`/api/systemd-services/${selected.id}/${serverId}/unit/read`, {});
      if (r.error) {
        setUnitMsg({ ok: false, text: r.error });
        setUnitLoaded(false);
        return;
      }
      setUnitFragment(r.fragmentPath || null);
      setUnitContent(r.content || '');
      setUnitLoaded(true);
      if (r.template) setUnitMsg({ ok: true, text: 'No file on this server yet — template loaded. Save to create it under /etc/systemd/system/.' });
      else setUnitMsg({ ok: true, text: 'Loaded from server.' });
    } catch (e: unknown) {
      setUnitMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
      setUnitLoaded(false);
    } finally {
      setUnitLoading(false);
    }
  };

  const saveUnitFile = async () => {
    if (!selected?.managedUnit || !serverId || !passwords[serverId]?.trim()) return;
    setUnitSaving(true);
    setUnitMsg(null);
    setSaveHint(false);
    try {
      const r = await postPw(`/api/systemd-services/${selected.id}/${serverId}/unit/save`, { content: unitContent });
      if (r.error) {
        setUnitMsg({ ok: false, text: r.error });
        return;
      }
      if (!r.success) {
        setUnitMsg({ ok: false, text: r.output || r.verifyOutput || 'Save failed' });
        return;
      }
      setUnitMsg({ ok: true, text: 'Saved. systemd units reloaded.' });
      setUnitLoaded(true);
      setSaveHint(true);
      void refreshStatus({ silent: true });
    } catch (e: unknown) {
      setUnitMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setUnitSaving(false);
    }
  };

  const sv = servers.find((s) => s.id === serverId);
  const st = serverId ? serverStatus[serverId] : undefined;
  const pwOk = !!(serverId && passwords[serverId]?.trim());
  const flags = computeUnitActionFlags(propsOut);

  const btn = (
    action: SystemdAction,
    label: string,
    icon: React.ReactNode,
    opts: { className?: string; hidden?: boolean } = {},
  ) => {
    if (opts.hidden) return null;
    return (
      <button
        type="button"
        className={`btn btn-sm svc-hub-act ${opts.className || ''}`}
        disabled={!!busy || !pwOk || !selected}
        onClick={() => run(action)}
        title={label}
      >
        {(busy === action || (action === 'status' && statusRefreshing)) ? <Loader2 size={14} className="spin" /> : icon}
        <span>{label}</span>
      </button>
    );
  };

  return (
    <div className="svc-hub-page hub-outlet">
      <div className="svc-hub-top-bar">
        <div className="svc-hub-top-brand">
          <Activity size={20} />
          <div>
            <h1 className="hub-page-h1">Services</h1>
            <p className="hub-page-sub">Catalog, systemctl actions over SSH, and managed unit files under /etc/systemd/system/</p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm svc-hub-rail-toggle"
          onClick={() => setRailOpen((o) => !o)}
          title={railOpen ? 'Hide list' : 'Show list'}
        >
          {railOpen ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
        </button>
      </div>

      <div className="dns-shell svc-hub-shell">
        <aside className={`svc-hub-rail ${railOpen ? 'open' : 'collapsed'}`}>
          <div className="svc-hub-rail-head">
            <input
              type="search"
              className="cfg-input svc-hub-search"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="btn btn-sm svc-hub-add" onClick={openAdd} title="Add service">
              <Plus size={16} />
            </button>
          </div>
          {loadingCatalog ? (
            <div className="svc-hub-rail-loading"><Loader2 size={20} className="spin" /></div>
          ) : (
            <ul className="svc-hub-list">
              {filtered.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`svc-hub-card ${selectedId === c.id ? 'active' : ''}`}
                    onClick={() => setSelectedId(c.id)}
                  >
                    <div className="svc-hub-card-top">
                      <span className="svc-hub-card-title">{c.label}</span>
                      <span className={`svc-hub-chip ${c.managedUnit ? 'managed' : 'actions'}`}>
                        {c.managedUnit ? 'Managed' : 'Actions'}
                      </span>
                    </div>
                    <code className="svc-hub-card-unit">{c.unit}</code>
                    {c.description ? <p className="svc-hub-card-desc">{c.description}</p> : null}
                    <div className="svc-hub-card-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm svc-hub-del"
                        onClick={(e) => { e.stopPropagation(); setDeleteId(c.id); }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!loadingCatalog && filtered.length === 0 && (
            <p className="svc-hub-empty">No entries match. Add a service or clear search.</p>
          )}
        </aside>

        <main className="svc-hub-main">
          {!selected ? (
            <div className="svc-hub-empty-panel">
              <div className="svc-hub-empty-icon"><ServerIcon size={40} strokeWidth={1} /></div>
              <h2>Select a service</h2>
              <p>Choose an entry from the list, or create one with the + button.</p>
              <button type="button" className="btn svc-hub-empty-cta" onClick={openAdd}>
                <Plus size={16} /> Add service
              </button>
            </div>
          ) : (
            <>
              <div className="svc-hub-detail-head">
                <div>
                  <h2>{selected.label}</h2>
                  <code className="svc-hub-detail-unit">{selected.unit}</code>
                  {selected.description ? <p className="svc-hub-detail-desc">{selected.description}</p> : null}
                </div>
                <div className="svc-hub-detail-meta">
                  <span className={`svc-hub-chip ${selected.managedUnit ? 'managed' : 'actions'}`}>
                    {selected.managedUnit ? 'Managed unit file' : 'Actions only'}
                  </span>
                </div>
              </div>

              <div className="svc-hub-toolbar">
                <label className="svc-hub-field">
                  <span>Server</span>
                  <select
                    className="commit-select"
                    value={serverId}
                    onChange={(e) => setServerId(e.target.value)}
                  >
                    {targets.length === 0 && <option value="">No servers for this entry</option>}
                    {targets.map((s) => (
                      <option key={s.id} value={s.id}>{s.label} — {s.host}</option>
                    ))}
                  </select>
                </label>
                {sv && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => testConnection(sv)}>
                    Test SSH
                  </button>
                )}
                {st?.status === 'online' && <span className="srv-status-tag online">Online</span>}
                {st?.status === 'offline' && <span className="srv-status-tag offline">Offline</span>}
                <label className="svc-hub-field svc-hub-pw">
                  <span>Password</span>
                  <input
                    type={showPw[serverId] ? 'text' : 'password'}
                    className="cfg-input"
                    placeholder="SSH password"
                    value={passwords[serverId] || ''}
                    onChange={(e) => setPassword(serverId, e.target.value)}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title={showPw[serverId] ? 'Hide' : 'Show'}
                    onClick={() => setShowPw((p) => ({ ...p, [serverId]: !p[serverId] }))}
                  >
                    {showPw[serverId] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </label>
              </div>

              <section className="svc-hub-section">
                <div className="svc-hub-section-head">
                  <h3 className="svc-hub-section-title">systemctl</h3>
                  <label className="svc-hub-auto">
                    <input
                      type="checkbox"
                      checked={autoStatus}
                      onChange={(e) => setAutoStatus(e.target.checked)}
                    />
                    <span>Auto status</span>
                    {statusRefreshing && <Loader2 size={12} className="spin svc-hub-auto-spin" aria-hidden />}
                    {flags.has && (flags.activeState || flags.unitFileState) && (
                      <span className="svc-hub-state-pill" title="ActiveState · UnitFileState">
                        {[flags.activeState, flags.unitFileState].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </label>
                </div>
                <div className="svc-hub-actions">
                  {btn('status', 'Status', <Activity size={14} />, { className: 'btn-ghost' })}
                  {btn('start', 'Start', <Play size={14} />, { hidden: flags.has && !flags.showStart })}
                  {btn('stop', 'Stop', <Square size={14} />, { hidden: flags.has && !flags.showStop })}
                  {btn('restart', 'Restart', <RotateCw size={14} />, { hidden: !flags.has })}
                  {btn('reload', 'Reload', <RefreshCw size={14} />, { hidden: flags.has && !flags.showReload })}
                  {btn('enable', 'Enable', <ToggleRight size={14} />, { hidden: flags.has && !flags.showEnable })}
                  {btn('disable', 'Disable', <ToggleLeft size={14} />, { hidden: flags.has && !flags.showDisable })}
                </div>
                {err && (
                  <div className="nginx-msg nginx-msg-err svc-hub-banner">
                    <AlertTriangle size={12} /> {err}
                  </div>
                )}
                {propsOut && Object.keys(propsOut).length > 0 && (
                  <div className="svc-hub-props">
                    <div className="svc-hub-props-title"><CheckCircle size={12} /> Properties</div>
                    <dl>
                      {Object.entries(propsOut).map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt>{k}</dt>
                          <dd>{v || '—'}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                )}
                {(textOut || busy) && (
                  <div className="svc-hub-output">
                    <div className="svc-hub-output-hdr">Output</div>
                    <pre className="svc-hub-pre">{textOut || (busy ? '…' : '')}</pre>
                  </div>
                )}
              </section>

              {selected.managedUnit ? (
                <section className="svc-hub-section svc-hub-unit-section">
                  <button
                    type="button"
                    className="svc-hub-accordion-hd"
                    onClick={() => setUnitOpen((o) => !o)}
                  >
                    <FileCode2 size={16} />
                    <span>Unit file</span>
                    <code className="svc-hub-path-hint">/etc/systemd/system/{selected.unit}</code>
                    {unitOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {unitOpen && (
                    <div className="svc-hub-accordion-body">
                      {unitFragment && (
                        <p className="svc-hub-fragment">
                          <strong>systemctl FragmentPath:</strong> <code>{unitFragment}</code>
                        </p>
                      )}
                      <p className="svc-hub-hint">
                        Only this path is edited. Use a unique unit name (e.g. <code>myapp.service</code>). After saving, run <strong>Start</strong> or <strong>Restart</strong> if the daemon should reload immediately.
                      </p>
                      <div className="svc-hub-unit-toolbar">
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={!pwOk || unitLoading}
                          onClick={loadUnitFile}
                        >
                          {unitLoading ? <Loader2 size={14} className="spin" /> : null}
                          Load from server
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-deploy"
                          disabled={!pwOk || unitSaving || !unitContent.trim()}
                          onClick={saveUnitFile}
                        >
                          {unitSaving ? <Loader2 size={14} className="spin" /> : null}
                          Save &amp; daemon-reload
                        </button>
                      </div>
                      {unitMsg && (
                        <div className={`nginx-msg ${unitMsg.ok ? 'nginx-msg-ok' : 'nginx-msg-err'} svc-hub-banner`}>
                          {unitMsg.text}
                        </div>
                      )}
                      {saveHint && (
                        <div className="nginx-msg nginx-msg-ok svc-hub-banner">
                          Run <strong>Start</strong> or <strong>Restart</strong> on this server if the service should apply changes immediately.
                        </div>
                      )}
                      <textarea
                        className="svc-hub-unit-ta"
                        value={unitContent}
                        onChange={(e) => setUnitContent(e.target.value)}
                        placeholder='Load from server, or paste a unit file. Blocks: [Unit], [Service], [Install]'
                        spellCheck={false}
                      />
                      {!unitContent.trim() && (
                        <p className="svc-hub-ta-placeholder">Tip: use “Load from server” for a starter template if the file does not exist yet.</p>
                      )}
                    </div>
                  )}
                </section>
              ) : (
                <section className="svc-hub-section svc-hub-muted">
                  <p>
                    <strong>Actions only</strong> — this entry controls an existing unit (e.g. from a package). To author a file under <code>/etc/systemd/system/</code>, edit the entry and enable <strong>Manage unit file on server</strong>.
                  </p>
                </section>
              )}
            </>
          )}
        </main>
      </div>

      {catalogErr && (
        <div className="svc-hub-toast err" role="alert">
          <AlertTriangle size={14} /> {catalogErr}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCatalogErr(null)}><XCircle size={14} /></button>
        </div>
      )}

      {modal && (
        <div className="svc-hub-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="svc-hub-modal" role="dialog" aria-labelledby="svc-modal-title">
            <div className="svc-hub-modal-hd">
              <h2 id="svc-modal-title">{modal === 'add' ? 'Add service' : 'Edit service'}</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal(null)}><XCircle size={18} /></button>
            </div>
            <div className="svc-hub-modal-body">
              <label className="svc-hub-modal-field">
                <span>Display name</span>
                <input className="cfg-input" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. Production API" />
              </label>
              <label className="svc-hub-modal-field">
                <span>Unit</span>
                <input
                  className="cfg-input mono"
                  value={form.unit}
                  onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                  placeholder="nginx.service or myapp.service"
                  spellCheck={false}
                />
              </label>
              <label className="svc-hub-modal-field">
                <span>Description (optional)</span>
                <textarea className="cfg-input svc-hub-modal-ta" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
              </label>
              <label className="svc-hub-check">
                <input
                  type="checkbox"
                  checked={form.managedUnit}
                  onChange={(e) => setForm((f) => ({ ...f, managedUnit: e.target.checked }))}
                />
                <span>Manage unit file on server (<code>/etc/systemd/system/&lt;name&gt;.service</code>)</span>
              </label>
              {form.managedUnit && (
                <p className="svc-hub-mini-hint">Unit must look like <code>myapp.service</code> (basename only).</p>
              )}
              <label className="svc-hub-check">
                <input
                  type="checkbox"
                  checked={form.restrictServers}
                  onChange={(e) => setForm((f) => ({ ...f, restrictServers: e.target.checked }))}
                />
                <span>Restrict to selected servers</span>
              </label>
              {form.restrictServers && (
                <div className="svc-hub-server-picks">
                  {servers.map((s) => (
                    <label key={s.id} className="svc-hub-check">
                      <input
                        type="checkbox"
                        checked={form.selectedServerIds.includes(s.id)}
                        onChange={(e) => {
                          setForm((f) => ({
                            ...f,
                            selectedServerIds: e.target.checked
                              ? [...f.selectedServerIds, s.id]
                              : f.selectedServerIds.filter((id) => id !== s.id),
                          }));
                        }}
                      />
                      <span>{s.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="svc-hub-modal-ft">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancel</button>
              <button type="button" className="btn btn-sm btn-deploy" disabled={savingCatalog} onClick={saveModal}>
                {savingCatalog ? <Loader2 size={14} className="spin" /> : null}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="svc-hub-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setDeleteId(null); }}>
          <div className="svc-hub-modal svc-hub-modal-sm" role="dialog">
            <div className="svc-hub-modal-hd">
              <h2>Remove entry?</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeleteId(null)}><XCircle size={18} /></button>
            </div>
            <p className="svc-hub-del-text">This only removes the dashboard catalog row. It does not delete unit files on the server.</p>
            <div className="svc-hub-modal-ft">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeleteId(null)}>Cancel</button>
              <button type="button" className="btn btn-sm btn-danger" disabled={deleting} onClick={confirmDelete}>
                {deleting ? <Loader2 size={14} className="spin" /> : null}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
